<template>
  <div class="adn-breadcrumbs">
    <span v-if="!hasBreadcrumbs" class="adn-breadcrumbs__back-link" data-test-back-button @click="onBackClick">
      <span class="adn-icon adn-icon--navigation-arrow-back" aria-hidden="true" />
      <span class="adn-breadcrumbs__back-link-text">{{ backLabel }}</span>
    </span>
    <ol v-else vocab="https://schema.org/" typeof="BreadcrumbList" class="adn-breadcrumbs__list" data-test-breadcrumbs>
      <template v-for="(breadcrumb, index) in breadcrumbs">
        <li
          v-if="displayItemWithStructuredData(index)"
          :key="index + 'router'"
          property="itemListElement"
          typeof="ListItem"
          class="adn-breadcrumbs__list-item"
          data-test-breadcrumbs-item
        >
          <router-link
            v-if="breadcrumb.route !== undefined"
            property="item"
            typeof="WebPage"
            :to="breadcrumb.route"
            class="adn-breadcrumbs__item-link"
            data-test-breadcrumbs-link
            @click="onClick($event, breadcrumb.route)"
          >
            <span property="name">{{ breadcrumb.name }}</span>
          </router-link>
          <a
            v-else-if="breadcrumb.link"
            data-test-breadcrumbs-link
            :href="breadcrumb.link"
            property="item"
            typeof="WebPage"
            class="adn-breadcrumbs__item-link"
          >
            <span property="name">{{ breadcrumb.name }}</span>
          </a>
          <span v-else data-test-breadcrumbs-simple-name>{{ breadcrumb.name }}</span>
          <meta property="position" :content="index + 1 + ''" />
          <span
            class="adn-icon adn-icon--navigation-direction-right adn-breadcrumbs__list-icon"
            :class="{ 'adn-breadcrumbs__list-icon--last': index == breadcrumbs.length - 2 }"
            aria-hidden="true"
          ></span>
        </li>
        <li
          v-else
          :key="index + 'span'"
          property="itemListElement"
          class="adn-breadcrumbs__list-item"
          data-test-breadcrumbs-item
        >
          <meta property="position" :content="index + 1 + ''" />
          <span data-test-breadcrumbs-simple-name>{{ breadcrumb.name }}</span>
        </li>
      </template>
    </ol>
  </div>
</template>

<script lang="ts">
import type { BreadCrumb } from '@b2ag/search/src/types/BreadCrumb'

export default {
  name: 'AdnBreadcrumbs',
  data() {
    return {
      /* Current page should not be include in structured data:
       * https://developers.google.com/search/docs/data-types/breadcrumb
       */
      displayLastItemWithStructuredData: false,
    }
  },
  props: {
    breadcrumbs: {
      type: Array<BreadCrumb>,
      default: [],
    },
    backLabel: {
      type: String,
      default: 'Retour',
    },
  },
  computed: {
    isCustomBack() {
      return this.breadcrumbs?.[0]?.name === 'customBack'
    },
    hasBreadcrumbs() {
      return (
        this.breadcrumbs !== undefined &&
        Array.isArray(this.breadcrumbs) &&
        this.breadcrumbs.length > 0 &&
        !this.isCustomBack
      )
    },
  },
  methods: {
    displayItemWithStructuredData(index) {
      return this.displayLastItemWithStructuredData || index + 1 < this.breadcrumbs.length
    },
    onClick(event, route) {
      /**
       * Emitted when a click is performed on one of the breadcrumbs links.
       *
       * @event linkClick
       * @type {event}
       */

      this.$emit('linkClick', event, route)
    },
    onBackClick() {
      if (this.isCustomBack) {
        this.onClick(null, this.breadcrumbs[0].route)
        // @ts-ignore check post-Vue3 migration
      } else if (this.$router) {
        // @ts-ignore check post-Vue3 migration
        this.$router.back()
      } else {
        // eslint-disable-next-line no-console
        console.warn('No back action possible')
      }
    },
  },
}
</script>

<style lang="scss">
.adn-breadcrumbs__list {
  display: flex;
  list-style-type: none;
  padding: 0;
  margin: 0;
  align-items: center;
  flex-wrap: wrap;

  .adn-breadcrumbs__list-icon {
    margin: 0 var(--spacing-2);
    font-size: var(--font-size-s);
    font-weight: 900;
    color: var(--color-text-light);

    &--last {
      color: var(--color-text);
    }
  }
}

.adn-breadcrumbs__list-item {
  font-size: var(--font-size-m);
  color: var(--color-text);
  margin: 0.5rem 0;

  &::first-letter {
    text-transform: uppercase;
  }
}

.adn-breadcrumbs__item-link {
  color: var(--color-text-light);
  border-bottom: 2px solid transparent;
  cursor: pointer;
  padding-bottom: 0.3rem;

  &:hover {
    color: var(--color-text-light);
    border-color: var(--color-text-light);
  }
}

.adn-breadcrumbs__back-link {
  color: var(--color-text-light);
  display: flex;
  align-items: center;
  width: fit-content;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  text-decoration: none;
  padding-bottom: 0.3rem;

  &:hover {
    color: var(--color-text-light);
    border-color: var(--color-text-light);
  }

  .adn-icon {
    margin-right: 0.5rem;
  }
}

.adn-breadcrumbs__back-link-text {
  font-size: var(--font-size-m);
}
</style>
